'use client';

import type { CSSProperties, ReactElement } from 'react';
import {
  Cmarkdownvisual,
  CmarkdownvisualError,
  CmarkdownvisualErrorReason,
  CmarkdownvisualType,
} from '../Cmarkdownvisual';
import { SequenceDiagramSvg } from './SequenceDiagramSvg';
import { useSequenceDiagram } from './useSequenceDiagram';

const SEQUENCE_METRIC_UNIT = 'MESSAGES';
const CONTAINER_MIN_HEIGHT = 280;

export interface CmarkdownsequenceProps {
  content: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Renders a Mermaid `sequenceDiagram` fenced block as a hand-rolled SVG widget,
 * mirroring the sibling Mermaid renderers (timeline/flowchart): parse → pure
 * layout → SVG body inside the shared `Cmarkdownvisual` chrome.
 *
 * The dispatcher hands this component every exact `sequenceDiagram` header, so
 * unparsable input surfaces as `CmarkdownvisualError` rather than vanishing.
 */
export function Cmarkdownsequence({
  content,
  className,
  style,
}: CmarkdownsequenceProps): ReactElement {
  const sequenceDiagram = useSequenceDiagram(content);

  if (!sequenceDiagram) {
    return (
      <CmarkdownvisualError
        type={CmarkdownvisualType.Sequence}
        reason={CmarkdownvisualErrorReason.Unparseable}
      />
    );
  }

  const containerHeight = Math.max(CONTAINER_MIN_HEIGHT, sequenceDiagram.layout.height);

  return (
    <Cmarkdownvisual
      type={CmarkdownvisualType.Sequence}
      title={sequenceDiagram.model.title}
      contentStyle={{ minHeight: containerHeight, flex: 1 }}
      className={className}
      style={style}
      metric={{ value: sequenceDiagram.messageCount, unit: SEQUENCE_METRIC_UNIT }}
    >
      <SequenceDiagramSvg layout={sequenceDiagram.layout} />
    </Cmarkdownvisual>
  );
}
